'use client';

import {
  Circle,
  GoogleMap,
  InfoWindow,
  Marker,
  MarkerClusterer,
  Polygon,
} from '@react-google-maps/api';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useGoogleMapsScript } from '../lib/google-maps/use-google-maps-script';
import {
  BaseMapProps,
  MapCoordinates,
  MapMarkerData,
  MapPolygonData,
} from '../features/inventaires/map-view/providers/types';
import { MapInfoWindowContent } from '../features/inventaires/map-view/providers/map-info-window-content';

export type GoogleMapLayerType = 'openstreetmap' | 'google-map' | 'satellite';

export interface GoogleMapEngineHandle {
  invalidateSize: () => void;
  setView: (center: { lat: number; lng: number }, zoom: number) => void;
  fitBounds: (
    points: Array<{ lat: number; lng: number }>,
    padding?: number,
  ) => void;
  getMap: () => google.maps.Map | null;
}

export interface GoogleMapEngineProps extends BaseMapProps {
  apiKey: string;
  defaultLayer?: GoogleMapLayerType;
  onLayerChange?: (layer: GoogleMapLayerType) => void;
  enableLayerControl?: boolean;
  /** Cloud-based Map ID (vector map) — required for rotation / Advanced Markers. */
  mapId?: string;
  /** Enable rotation gestures (requires `mapId`). Default true when mapId provided. */
  enableRotation?: boolean;
  /** Enable the pegman (Street View) control. Default true. */
  showStreetViewControl?: boolean;
  /** Enable Google Maps camera control button (2D/3D). Default true. */
  showCameraControl?: boolean;
  /**
   * Position of the native map type (Plan/Satellite) control.
   * Default TOP_RIGHT. Use TOP_LEFT on dashboard to avoid filter FAB overlap.
   */
  mapTypeControlPosition?: 'TOP_LEFT' | 'TOP_RIGHT';
  /**
   * Side for native zoom / Street View / rotate controls.
   * Use LEFT on dashboard so bottom-right custom FABs do not overlap.
   */
  sideControlsPosition?: 'LEFT' | 'RIGHT';
  /** User GPS position — rendered as a blue location marker. */
  userLocation?: MapCoordinates | null;
  /**
   * Increment to force pan to `options.center` even when lat/lng are unchanged
   * (e.g. recenter button after the user panned away).
   */
  forceCenterRevision?: number;
  /** Search radius circle (meters) centered on options.center. */
  searchRadius?: number;
  showRadiusCircle?: boolean;
}

const DEFAULT_CLUSTER_STYLES = [
  { color: '#51cf66', size: 40, textSize: 12 },
  { color: '#ffd43b', size: 45, textSize: 13 },
  { color: '#ff8c00', size: 50, textSize: 14 },
  { color: '#ff6b6b', size: 55, textSize: 15 },
  { color: '#8b0000', size: 60, textSize: 16 },
];

function buildClusterIcon(size: number, color: string): string {
  const center = size / 2;
  const radius = size / 2 - 2;
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">` +
    `<circle cx="${center}" cy="${center}" r="${radius}" fill="${color}" stroke="white" stroke-width="2"/>` +
    `</svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

function getClusterStylesForGoogle(): Array<{
  textColor: string;
  url: string;
  height: number;
  width: number;
  textSize: number;
}> {
  return DEFAULT_CLUSTER_STYLES.map((style) => ({
    textColor: 'white',
    url: buildClusterIcon(style.size, style.color),
    height: style.size,
    width: style.size,
    textSize: style.textSize,
  }));
}

function mapLayerToGoogleType(
  layer: GoogleMapLayerType,
): google.maps.MapTypeId | string {
  switch (layer) {
    case 'satellite':
      return 'hybrid';
    case 'google-map':
    case 'openstreetmap':
    default:
      return 'roadmap';
  }
}

function googleTypeToMapLayer(typeId: string | undefined): GoogleMapLayerType {
  if (typeId === 'hybrid' || typeId === 'satellite') return 'satellite';
  return 'google-map';
}

export const GoogleMapEngine = forwardRef<
  GoogleMapEngineHandle,
  GoogleMapEngineProps
>(function GoogleMapEngine(
  {
    apiKey,
    options,
    markers = [],
    polygons = [],
    clustering = { enabled: true },
    eventHandlers,
    renderInfoWindow,
    loading = false,
    className = '',
    style = { width: '100%', height: '100%' },
    defaultLayer = 'satellite',
    onLayerChange,
    enableLayerControl = true,
    mapId,
    enableRotation,
    showStreetViewControl = true,
    showCameraControl = true,
    mapTypeControlPosition = 'TOP_RIGHT',
    sideControlsPosition = 'RIGHT',
    userLocation = null,
    forceCenterRevision = 0,
    searchRadius,
    showRadiusCircle = false,
  },
  ref,
) {
  const { isLoaded, loadError } = useGoogleMapsScript({ apiKey });

  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [selectedMarker, setSelectedMarker] = useState<MapMarkerData | null>(null);
  const [selectedPolygon, setSelectedPolygon] = useState<MapPolygonData | null>(null);
  const isProgrammaticMove = useRef(false);

  const clustererKey = useMemo(() => {
    const radius = clustering.maxClusterRadius ?? 60;
    return `${clustering.enabled ? 'on' : 'off'}-${markers.length}-${radius}`;
  }, [clustering.enabled, clustering.maxClusterRadius, markers.length]);

  const rotationEnabled = enableRotation ?? Boolean(mapId);

  const mapOptions: google.maps.MapOptions = useMemo(
    () => {
      const mapsApi = typeof window !== 'undefined' ? window.google?.maps : undefined;
      const mapTypePosition = mapsApi
        ? mapTypeControlPosition === 'TOP_LEFT'
          ? mapsApi.ControlPosition.TOP_LEFT
          : mapsApi.ControlPosition.TOP_RIGHT
        : undefined;
      const zoomPosition = mapsApi
        ? sideControlsPosition === 'LEFT'
          ? mapsApi.ControlPosition.LEFT_CENTER
          : mapsApi.ControlPosition.RIGHT_CENTER
        : undefined;
      const bottomSidePosition = mapsApi
        ? sideControlsPosition === 'LEFT'
          ? mapsApi.ControlPosition.LEFT_BOTTOM
          : mapsApi.ControlPosition.RIGHT_BOTTOM
        : undefined;
      return {
        mapTypeId: mapLayerToGoogleType(defaultLayer),
        mapTypeControl: options.mapTypeControl ?? true,
        mapTypeControlOptions: {
          position: mapTypePosition,
          style: mapsApi ? mapsApi.MapTypeControlStyle.DROPDOWN_MENU : undefined,
        },
        zoomControl: options.zoomControl ?? false,
        zoomControlOptions: {
          position: zoomPosition,
        },
        fullscreenControl: options.fullscreenControl ?? false,
        streetViewControl: showStreetViewControl,
        streetViewControlOptions: {
          position: bottomSidePosition,
        },
        ...(showCameraControl
          ? {}
          : ({ cameraControl: false } as google.maps.MapOptions)),
        gestureHandling: 'greedy',
        clickableIcons: false,
        mapId,
        // Vector map only — fall back to false when mapId is missing.
        headingInteractionEnabled: rotationEnabled,
        tiltInteractionEnabled: rotationEnabled,
        rotateControl: rotationEnabled,
        rotateControlOptions: {
          position: bottomSidePosition,
        },
      };
    },
    [
      defaultLayer,
      options.mapTypeControl,
      options.zoomControl,
      options.fullscreenControl,
      mapId,
      rotationEnabled,
      showStreetViewControl,
      showCameraControl,
      mapTypeControlPosition,
      sideControlsPosition,
    ],
  );

  const onLoad = useCallback((instance: google.maps.Map) => {
    setMap(instance);
  }, []);

  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      invalidateSize: () => {
        if (!map || typeof window === 'undefined' || !window.google?.maps) return;
        window.google.maps.event.trigger(map, 'resize');
      },
      setView: (center, zoom) => {
        if (!map) return;
        isProgrammaticMove.current = true;
        map.setCenter({ lat: center.lat, lng: center.lng });
        map.setZoom(zoom);
        window.setTimeout(() => {
          isProgrammaticMove.current = false;
        }, 100);
      },
      fitBounds: (points, padding = 48) => {
        if (!map || points.length === 0) return;
        if (typeof window === 'undefined' || !window.google?.maps) return;
        isProgrammaticMove.current = true;
        const bounds = new window.google.maps.LatLngBounds();
        points.forEach((point) => {
          bounds.extend({ lat: point.lat, lng: point.lng });
        });
        map.fitBounds(bounds, padding);
        window.setTimeout(() => {
          isProgrammaticMove.current = false;
        }, 150);
      },
      getMap: () => map,
    }),
    [map],
  );

  useEffect(() => {
    if (!map || !options.center) return;
    if (isProgrammaticMove.current) return;
    isProgrammaticMove.current = true;
    map.setCenter({ lat: options.center.lat, lng: options.center.lng });
    window.setTimeout(() => {
      isProgrammaticMove.current = false;
    }, 100);
  }, [map, options.center?.lat, options.center?.lng]);

  useEffect(() => {
    if (!map || !options.center || forceCenterRevision <= 0) return;
    isProgrammaticMove.current = true;
    map.panTo({ lat: options.center.lat, lng: options.center.lng });
    window.setTimeout(() => {
      isProgrammaticMove.current = false;
    }, 100);
  }, [map, forceCenterRevision, options.center?.lat, options.center?.lng]);

  useEffect(() => {
    if (!map || options.zoom === undefined) return;
    if (isProgrammaticMove.current) return;
    isProgrammaticMove.current = true;
    map.setZoom(options.zoom);
    window.setTimeout(() => {
      isProgrammaticMove.current = false;
    }, 100);
  }, [map, options.zoom]);

  const handleMapClick = useCallback(
    (event: google.maps.MapMouseEvent) => {
      if (event.latLng && eventHandlers?.onMapClick) {
        eventHandlers.onMapClick({
          lat: event.latLng.lat(),
          lng: event.latLng.lng(),
        });
      }
    },
    [eventHandlers],
  );

  const handleMapMove = useCallback(() => {
    if (!map || !eventHandlers?.onMapMove) return;
    if (isProgrammaticMove.current) return;
    const center = map.getCenter();
    const zoom = map.getZoom();
    if (!center || zoom === undefined) return;
    eventHandlers.onMapMove({ lat: center.lat(), lng: center.lng() }, zoom);
  }, [map, eventHandlers]);

  const handleBoundsChanged = useCallback(() => {
    if (!map || !eventHandlers?.onBoundsChanged) return;
    const bounds = map.getBounds();
    if (!bounds) return;
    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();
    eventHandlers.onBoundsChanged({
      north: ne.lat(),
      south: sw.lat(),
      east: ne.lng(),
      west: sw.lng(),
    });
  }, [map, eventHandlers]);

  const handleMapTypeChanged = useCallback(() => {
    if (!map || !onLayerChange) return;
    const next = googleTypeToMapLayer(map.getMapTypeId() ?? undefined);
    onLayerChange(next);
  }, [map, onLayerChange]);

  const handleMarkerClick = useCallback(
    (marker: MapMarkerData) => {
      setSelectedPolygon(null);
      setSelectedMarker(marker);
      if (eventHandlers?.onMarkerClick) {
        eventHandlers.onMarkerClick(marker);
      }
    },
    [eventHandlers],
  );

  const handlePolygonClick = useCallback(
    (polygon: MapPolygonData) => {
      setSelectedMarker(null);
      setSelectedPolygon(polygon);
      if (eventHandlers?.onPolygonClick) {
        eventHandlers.onPolygonClick(polygon);
      }
    },
    [eventHandlers],
  );

  const handleViewDetails = useCallback(
    (inventaireId: string) => {
      setSelectedMarker(null);
      setSelectedPolygon(null);
      eventHandlers?.onViewDetails?.(inventaireId);
    },
    [eventHandlers],
  );

  const renderMarkerInfoWindow = (marker: MapMarkerData) => (
    <InfoWindow
      position={{ lat: marker.position.lat, lng: marker.position.lng }}
      onCloseClick={() => setSelectedMarker(null)}
      options={{
        pixelOffset:
          typeof window !== 'undefined' && window.google?.maps
            ? new window.google.maps.Size(0, -10)
            : undefined,
        maxWidth: 320,
        minWidth: 250,
      }}
    >
      <>
        {renderInfoWindow ? (
          renderInfoWindow(marker)
        ) : (
          <MapInfoWindowContent
            marker={marker}
            onViewDetails={handleViewDetails}
          />
        )}
      </>
    </InfoWindow>
  );

  const renderPolygonInfoWindow = (polygon: MapPolygonData) => {
    const center = polygon.center;
    if (!center) return null;
    const syntheticMarker: MapMarkerData = {
      id: polygon.id,
      position: center,
      title: polygon.title,
      description: polygon.description,
      data: polygon.data,
    };
    return (
      <InfoWindow
        position={{ lat: center.lat, lng: center.lng }}
        onCloseClick={() => setSelectedPolygon(null)}
        options={{
          pixelOffset:
            typeof window !== 'undefined' && window.google?.maps
              ? new window.google.maps.Size(0, -10)
              : undefined,
          maxWidth: 320,
          minWidth: 250,
        }}
      >
        <>
          {renderInfoWindow ? (
            renderInfoWindow(syntheticMarker)
          ) : (
            <MapInfoWindowContent
              marker={syntheticMarker}
              onViewDetails={handleViewDetails}
            />
          )}
        </>
      </InfoWindow>
    );
  };

  if (!apiKey || loadError) {
    return (
      <div
        className={`flex items-center justify-center bg-gray-100 ${className}`}
        style={style}
      >
        <div className="p-4 text-center">
          <p className="font-medium text-red-600">
            {!apiKey
              ? 'Clé Google Maps manquante'
              : 'Erreur de chargement de Google Maps'}
          </p>
          <p className="text-sm text-gray-600">
            {!apiKey
              ? 'Définir GOOGLE_MAPS_API_KEY dans la configuration.'
              : loadError?.message ?? 'Échec du chargement.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`relative ${className}`} style={style}>
      {(loading || !isLoaded) && (
        <div className="pointer-events-none absolute inset-0 z-[999] flex items-center justify-center bg-black/10">
          <div className="flex items-center space-x-3 rounded-lg bg-white p-3 shadow-lg">
            <div className="h-5 w-5 animate-spin rounded-full border-b-2 border-green-600" />
            <span className="text-sm">Chargement de la carte...</span>
          </div>
        </div>
      )}

      {isLoaded && (
        <GoogleMap
          mapContainerStyle={style}
          center={{ lat: options.center.lat, lng: options.center.lng }}
          zoom={options.zoom}
          options={mapOptions}
          onLoad={onLoad}
          onUnmount={onUnmount}
          onClick={handleMapClick}
          onDragEnd={handleMapMove}
          onZoomChanged={handleMapMove}
          onBoundsChanged={handleBoundsChanged}
          onMapTypeIdChanged={handleMapTypeChanged}
        >
          {clustering.enabled ? (
            <MarkerClusterer
              key={clustererKey}
              options={{
                maxZoom: 20,
                gridSize: clustering.maxClusterRadius || 60,
                averageCenter: true,
                ignoreHidden: true,
                styles: getClusterStylesForGoogle(),
              }}
            >
              {(clusterer) => (
                <>
                  {markers.map((marker) => (
                    <Marker
                      key={marker.id}
                      position={{
                        lat: marker.position.lat,
                        lng: marker.position.lng,
                      }}
                      title={marker.title}
                      clusterer={clusterer}
                      onClick={() => handleMarkerClick(marker)}
                      icon={
                        marker?.data?.icon
                          ? {
                              url: marker.data.icon,
                              scaledSize:
                                typeof window !== 'undefined' && window.google?.maps
                                  ? new window.google.maps.Size(30, 30)
                                  : undefined,
                            }
                          : undefined
                      }
                    />
                  ))}
                  {selectedMarker && renderMarkerInfoWindow(selectedMarker)}
                </>
              )}
            </MarkerClusterer>
          ) : (
            <>
              {markers.map((marker) => (
                <Marker
                  key={marker.id}
                  position={{
                    lat: marker.position.lat,
                    lng: marker.position.lng,
                  }}
                  title={marker.title}
                  onClick={() => handleMarkerClick(marker)}
                  icon={
                    marker?.data?.icon
                      ? {
                          url: marker.data.icon,
                          scaledSize:
                            typeof window !== 'undefined' && window.google?.maps
                              ? new window.google.maps.Size(30, 30)
                              : undefined,
                        }
                      : undefined
                  }
                />
              ))}
              {selectedMarker && renderMarkerInfoWindow(selectedMarker)}
            </>
          )}

          {polygons.map((polygon) => (
            <Polygon
              key={polygon.id}
              paths={polygon.coordinates.map((c: MapCoordinates) => ({
                lat: c.lat,
                lng: c.lng,
              }))}
              options={{
                fillColor: polygon.fillColor ?? '#22c55e',
                fillOpacity: polygon.fillOpacity ?? 0.3,
                strokeColor: polygon.strokeColor ?? '#15803d',
                strokeOpacity: polygon.strokeOpacity ?? 1,
                strokeWeight: polygon.strokeWeight ?? 3,
                clickable: true,
              }}
              onClick={() => handlePolygonClick(polygon)}
            />
          ))}

          {selectedPolygon && renderPolygonInfoWindow(selectedPolygon)}

          {showRadiusCircle && searchRadius && options.center && (
            <Circle
              center={{ lat: options.center.lat, lng: options.center.lng }}
              radius={searchRadius}
              options={{
                strokeColor: '#3b82f6',
                strokeOpacity: 0.9,
                strokeWeight: 2,
                fillColor: '#3b82f6',
                fillOpacity: 0.1,
                clickable: false,
              }}
            />
          )}

          {userLocation && (
            <>
              <Circle
                center={{ lat: userLocation.lat, lng: userLocation.lng }}
                radius={120}
                options={{
                  strokeColor: '#38bdf8',
                  strokeOpacity: 0.9,
                  strokeWeight: 2,
                  fillColor: '#38bdf8',
                  fillOpacity: 0.15,
                  clickable: false,
                }}
              />
              <Marker
                position={{ lat: userLocation.lat, lng: userLocation.lng }}
                title="Ma position"
                zIndex={10000}
                icon={
                  typeof window !== 'undefined' && window.google?.maps
                    ? {
                        path: window.google.maps.SymbolPath.CIRCLE,
                        scale: 8,
                        fillColor: '#0ea5e9',
                        fillOpacity: 1,
                        strokeColor: '#ffffff',
                        strokeWeight: 2,
                      }
                    : undefined
                }
              />
            </>
          )}
        </GoogleMap>
      )}
    </div>
  );
});

GoogleMapEngine.displayName = 'GoogleMapEngine';
